import { useQuery } from '@tanstack/react-query';
import { Dialog } from 'sharedUi/Dialog';
import { Button } from 'sharedUi/Button';
import { errorMasterService, type ErrorMasterDependencies } from '../../services/error-master.service';

interface ErrorMasterDeleteDialogProps {
  /** The error master being deleted; null closes the dialog. */
  errorId: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  deleting?: boolean;
  testId?: string;
}

/**
 * Delete dialog for an error master that first checks server-side dependencies.
 *  - While a notification trigger still references this record's errorCode, deletion
 *    is BLOCKED and the notifications are listed so the admin knows what to detach.
 *  - Otherwise it behaves like a normal delete confirmation.
 * Mirrors the backend, which refuses the same delete with a 409.
 */
const ErrorMasterDeleteDialog = ({
  errorId,
  onCancel,
  onConfirm,
  deleting = false,
  testId = 'ErrorMasterDeleteDialog',
}: ErrorMasterDeleteDialogProps) => {
  const { data, isLoading, isError } = useQuery<ErrorMasterDependencies>({
    queryKey: ['error-master', 'dependencies', errorId],
    queryFn: () => errorMasterService.getDependencies(errorId as string),
    enabled: !!errorId,
    staleTime: 0,
  });

  const blocked = !!data && !data.canDelete;
  const notifications = data?.dependents?.notifications ?? [];

  const footer = (
    <div className="flex justify-end gap-3">
      <Button
        label={blocked ? 'Close' : 'Cancel'}
        severity="secondary"
        outlined
        size="small"
        onClick={onCancel}
        disabled={deleting}
      />
      {!blocked && (
        <Button
          label={deleting ? 'Deleting...' : 'Delete'}
          severity="danger"
          size="small"
          onClick={onConfirm}
          disabled={deleting || isLoading || isError}
          icon={deleting ? 'pi pi-spin pi-spinner' : undefined}
        />
      )}
    </div>
  );

  return (
    <Dialog
      visible={!!errorId}
      onHide={onCancel}
      header={blocked ? 'Cannot delete this error master' : 'Confirm Delete'}
      footer={footer}
      style={{ width: '480px', maxWidth: '95vw' }}
    >
      <div data-testid={testId}>
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-ink-muted">
            <i className="pi pi-spin pi-spinner" />
            Checking dependencies...
          </div>
        ) : isError ? (
          <div className="flex items-start gap-3">
            <i className="pi pi-exclamation-triangle flex-shrink-0 text-2xl text-amber-500" />
            <p className="m-0 text-sm leading-relaxed text-ink-muted">
              Could not verify dependencies. Please try again.
            </p>
          </div>
        ) : blocked ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <i className="pi pi-ban flex-shrink-0 text-2xl text-rose-500" />
              <p className="m-0 text-sm leading-relaxed text-ink-muted">
                The error code{' '}
                <span className="font-mono text-gray-800 dark:text-gray-200">{data?.errorCode}</span>{' '}
                is still used by the notification trigger(s) below. Remove it from these
                notifications first, then delete it.
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-800">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                  Notifications
                </span>
                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                  {notifications.length}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {notifications.map((n) => (
                  <span
                    key={n._id}
                    className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                  >
                    {n.name || n._id}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <i className="pi pi-exclamation-triangle flex-shrink-0 text-2xl text-amber-500" />
            <p className="m-0 text-sm leading-relaxed text-ink-muted">
              Are you sure you want to delete this error master? This action cannot be undone.
            </p>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ErrorMasterDeleteDialog;
